import { createContext } from 'react';

import { useGuardedContext } from '@/hooks/useGuardedContext';
import { AiSuggestion_DispositiveProvisionFragment } from '@/modules/aiDP/graphql/aiDP.generated';
import { MatchableKind } from '@/modules/aiSuggestions/aiSuggestions.types';
import { AISuggestionsMatcherVariant } from '@/modules/aiSuggestions/AISuggestionsMatcher/AISuggestionsMatcher.types';
import { KgRootKind } from '@/types/schema';

export interface DPSuggestionKinds {
  aiSuggestionKind: MatchableKind;
  kgRootKind: KgRootKind;
  matcherVariantKind: AISuggestionsMatcherVariant;
}

export interface DPFormSuggestionsContext {
  loading: boolean;

  /**
   * Depending on which primary client death form we're on, these are the
   * suggestion kinds we should use. For example, if we're on the first grantor
   * death form, we will use DP_RECIPIENT_GRANTOR_1_DIES_FIRST instead of
   * DP_RECIPIENT_GRANTOR_2_DIES_FIRST, for the recipient suggestions.
   */
  dpSuggestionKinds: DPSuggestionKinds | undefined;

  /**
   * The dp recipient suggestions, for the current primary client death form.
   */
  recipientSuggestions: AiSuggestion_DispositiveProvisionFragment[] | undefined;
  unacknowledgedRecipientSuggestions: AiSuggestion_DispositiveProvisionFragment[];

  /**
   * Indicates if there are any suggestions for the current primary client death form.
   */
  hasSuggestions: boolean;

  /**
   * Indicates if there are any unacked recipient suggestions,
   * for the current primary client death form.
   */
  hasUnacknowledgedRecipientSuggestions: boolean;

  /**
   * Indicates if there are any unacknowledged KGV2 DP recipient suggestions
   * for the current primary client death form.
   */
  hasUnacknowledgedKGV2RecipientSuggestions: boolean;

  /**
   * Whether the opposite death order has unacknowledged suggestions.
   */
  oppositeDeathOrderHasUnacknowledgedSuggestions: boolean;

  /**
   * Count of unacknowledged suggestions for the opposite death order.
   */
  oppositeDeathOrderUnacknowledgedCount: number;

  /**
   * First name of the client who dies first in the opposite death order.
   */
  oppositeDeathOrderClientName: string;

  /**
   * ID of the client who dies first in the opposite death order.
   */
  oppositeDeathOrderClientId: string | null;

  /**
   * Whether the opposite death order is the "primary" (first client in primaryClients array).
   */
  isOppositeDeathOrderPrimary: boolean;
}

export const DPFormSuggestionsContext = createContext<DPFormSuggestionsContext>(
  {
    loading: true,
    dpSuggestionKinds: undefined,

    // these are unacked or acked suggestions
    hasSuggestions: false,
    recipientSuggestions: undefined,

    // there are unacked suggestions
    hasUnacknowledgedRecipientSuggestions: false,
    hasUnacknowledgedKGV2RecipientSuggestions: false,
    unacknowledgedRecipientSuggestions: [],

    // opposite death order suggestions
    oppositeDeathOrderHasUnacknowledgedSuggestions: false,
    oppositeDeathOrderUnacknowledgedCount: 0,
    oppositeDeathOrderClientName: '',
    oppositeDeathOrderClientId: null,
    isOppositeDeathOrderPrimary: false,
  }
);

export const useDPFormSuggestionsContext = () => {
  return useGuardedContext(
    DPFormSuggestionsContext,
    'DPFormSuggestionsContextProvider'
  );
};
